import React from "react";
import {
    ChevronDown16Regular,
    ChevronRight16Regular,
    Archive20Regular,
    ArrowSync20Regular,
    Calendar20Regular,
    Delete20Regular,
    Drafts20Regular,
    Folder20Regular,
    MailInboxAll20Regular,
    Send20Regular,
    Settings20Regular,
} from "@fluentui/react-icons";
import type { MailboxAccess, MailFolder } from "../../../services/graphMailService";
import { alleDokumente } from "../../windowManager/browserFenster/fensterRegister";
import { useSheetKlasse } from "../../vorgang/v4/mobil/sheetKlasse";
import { buildMailFolderOrder, visibleFolderRows, zeigeVollenBaum, type MailFolderEntry } from "../mailFolderTree";
import { browserSpeicher, leseAufgeklappt, schreibeAufgeklappt } from "../mailFolderExpansion";
import { postfachKuerzel, postfachName, zahlLabel } from "./mobilAnzeige";
import "./mailMobil.css";

const ORDNER_ICONS: Record<string, React.ReactNode> = {
    inbox: <MailInboxAll20Regular />,
    drafts: <Drafts20Regular />,
    sentitems: <Send20Regular />,
    deleteditems: <Delete20Regular />,
    archive: <Archive20Regular />,
};
const ordnerIcon = (e: MailFolderEntry): React.ReactNode =>
    e.virtual ? <Calendar20Regular /> : (e.alias ? ORDNER_ICONS[e.alias] : null) ?? <Folder20Regular />;

export interface MailPostfachMenueProps {
    offen: boolean;
    onSchliessen: () => void;
    /** Lesbare Postfächer (bereits `canReadMailbox`-gefiltert). */
    postfaecher: MailboxAccess[];
    folderTrees: Record<string, MailFolder[]>;
    unreadByMailbox: Record<string, number>;
    currentMailbox: string | null;
    /** Aktiver Ordner (null = Posteingang). */
    activeFolderId: string | null;
    isAdmin: boolean;
    /** Postfach + Ordner setzen — Semantik wie das Mail-Menü der Sidebar. */
    onWaehle: (mailbox: string, folder: { id: string; displayName: string } | null) => void;
    onSynchronisieren?: () => void;
    onEinstellungen?: () => void;
}

/**
 * Seitenmenü wie in Outlook iOS: links jedes lesbare Postfach als Kreis,
 * rechts die Ordner des dort gewählten Postfachs. Ein Kreis zeigt erst nur
 * dessen Ordner — gewechselt wird mit dem Ordner (auch dem Posteingang).
 *
 * Ordner-Reihenfolge und -Umfang wie in der Sidebar: `buildMailFolderOrder`,
 * voller Baum nach `zeigeVollenBaum` (eigenes Postfach, Admin, schaden@).
 */
export const MailPostfachMenue: React.FC<MailPostfachMenueProps> = ({
    offen,
    onSchliessen,
    postfaecher,
    folderTrees,
    unreadByMailbox,
    currentMailbox,
    activeFolderId,
    isAdmin,
    onWaehle,
    onSynchronisieren,
    onEinstellungen,
}) => {
    const [gezeigt, setGezeigt] = React.useState<string | null>(currentMailbox);
    // Beim Öffnen immer mit dem aktiven Postfach beginnen.
    React.useEffect(() => {
        if (offen) setGezeigt(currentMailbox);
    }, [offen, currentMailbox]);
    // Der Melde-Knopf tritt zurück, solange das Menü offen ist.
    useSheetKlasse(offen);
    React.useEffect(() => {
        if (!offen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onSchliessen();
        };
        alleDokumente.addEventListener("keydown", onKey);
        return () => alleDokumente.removeEventListener("keydown", onKey);
    }, [offen, onSchliessen]);

    // Auf-/zugeklappte Ordner — derselbe gespeicherte Stand wie in der
    // Desktop-Sidebar (Schlüssel `postfach:ordner`).
    const [aufgeklappt, setAufgeklappt] = React.useState<Set<string>>(() => leseAufgeklappt(browserSpeicher()));
    const schalteOrdner = (mailbox: string, key: string) =>
        setAufgeklappt((prev) => {
            const next = new Set(prev);
            const id = `${mailbox}:${key}`;
            if (next.has(id)) next.delete(id);
            else next.add(id);
            schreibeAufgeklappt(browserSpeicher(), next);
            return next;
        });

    const mb = postfaecher.find((m) => m.mailboxAddress === gezeigt) ?? postfaecher[0] ?? null;
    const eintraege = React.useMemo(() => {
        if (!mb) return [];
        return buildMailFolderOrder(folderTrees[mb.mailboxAddress] ?? [], {
            includeCustom: zeigeVollenBaum({ isOwn: mb.isOwn, isAdmin, vollerOrdnerbaum: mb.vollerOrdnerbaum }),
            virtualTermine: { unread: 0 },
        });
    }, [mb, folderTrees, isAdmin]);

    if (!offen) return null;
    const aktiverKey = activeFolderId ?? "inbox";

    return (
        <>
            <div className="mm-scrim" onClick={onSchliessen} aria-hidden />
            <nav className="mm-menue" aria-label="Postfach und Ordner wählen">
                <div className="mm-menue-leiste">
                    {postfaecher.map((p) => {
                        const ungelesen = (unreadByMailbox[p.mailboxAddress] ?? 0) > 0;
                        return (
                            <button
                                key={p.id}
                                type="button"
                                className="mm-menue-kreis"
                                aria-current={p.mailboxAddress === mb?.mailboxAddress}
                                aria-label={postfachName(p.displayName, p.mailboxAddress)}
                                title={p.mailboxAddress}
                                onClick={() => setGezeigt(p.mailboxAddress)}
                            >
                                <span className="mm-kreis">
                                    {postfachKuerzel(p.displayName, p.mailboxAddress)}
                                    {ungelesen && <span className="mm-punkt" />}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <div className="mm-menue-haupt">
                    <div className="mm-menue-kopf">
                        <div className="mm-menue-name">{mb ? postfachName(mb.displayName, mb.mailboxAddress) : "Kein Postfach"}</div>
                        {mb?.displayName && <div className="mm-menue-adresse">{mb.mailboxAddress}</div>}
                    </div>
                    <div className="mm-menue-ordner">
                        {mb &&
                            visibleFolderRows(eintraege, (key) => aufgeklappt.has(`${mb.mailboxAddress}:${key}`)).map((e) => {
                                const aktiv = mb.mailboxAddress === currentMailbox && aktiverKey === e.key;
                                const zahl = zahlLabel(
                                    e.alias === "inbox" ? unreadByMailbox[mb.mailboxAddress] ?? e.unread : e.unread,
                                );
                                const offen = aufgeklappt.has(`${mb.mailboxAddress}:${e.key}`);
                                const klappbar = e.hasChildren || !!e.kinderFehlen;
                                return (
                                    <div key={e.key} className="mm-ordner-zeile" style={{ paddingLeft: e.depth * 16 }}>
                                        {klappbar ? (
                                            <button
                                                type="button"
                                                className="mm-ordner-pfeil"
                                                aria-expanded={offen}
                                                aria-label={`${e.label} ${offen ? "zuklappen" : "aufklappen"}`}
                                                onClick={() => schalteOrdner(mb.mailboxAddress, e.key)}
                                            >
                                                {offen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                                            </button>
                                        ) : (
                                            <span className="mm-ordner-pfeil" aria-hidden />
                                        )}
                                        <button
                                            type="button"
                                            className="mm-sheet-zeile"
                                            aria-current={aktiv}
                                            onClick={() => {
                                                onWaehle(mb.mailboxAddress, { id: e.key, displayName: e.label });
                                                onSchliessen();
                                            }}
                                        >
                                            <span style={{ display: "flex", flexShrink: 0 }}>{ordnerIcon(e)}</span>
                                            <span className="mm-label">{e.label}</span>
                                            {zahl && <span className="mm-zahl">{zahl}</span>}
                                        </button>
                                    </div>
                                );
                            })}
                    </div>
                    {(onSynchronisieren || onEinstellungen) && (
                        <div className="mm-menue-fuss">
                            {onSynchronisieren && (
                                <button
                                    type="button"
                                    className="mm-sheet-zeile"
                                    onClick={() => {
                                        onSynchronisieren();
                                        onSchliessen();
                                    }}
                                >
                                    <ArrowSync20Regular />
                                    <span className="mm-label">Synchronisieren</span>
                                </button>
                            )}
                            {onEinstellungen && (
                                <button
                                    type="button"
                                    className="mm-sheet-zeile"
                                    onClick={() => {
                                        onEinstellungen();
                                        onSchliessen();
                                    }}
                                >
                                    <Settings20Regular />
                                    <span className="mm-label">Einstellungen</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </nav>
        </>
    );
};
